import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Phone,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  Trash2,
  Eye,
  MessageSquare,
  ExternalLink,
  MoreHorizontal,
  Search,
  Filter,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DeleteAlertDialog } from '@/components/admin/DeleteAlertDialog';
import { useToast } from '@/hooks/use-toast';
import { contactApi, ContactMessage } from '@/lib/api';
import { getWhatsAppLink, getEmailLink } from '@/lib/config';

const serviceLabels: Record<string, string> = {
  branding: 'Branding & Identidade Visual',
  uiux: 'UI/UX Design',
  campanhas: 'Direção de Arte & Campanhas',
  editorial: 'Design Editorial',
  ilustracao: 'Ilustração & Arte Digital',
  motion: 'Motion Graphics & Vídeo',
  ia: 'IA & Personagens Digitais',
  fullstack: 'Desenvolvimento Fullstack',
  '3d': 'Modelagem 3D',
  consultoria: 'Consultoria',
  outro: 'Outro'
};

const budgetLabels: Record<string, string> = {
  '5k': 'Até R$ 5.000',
  '10k': 'R$ 5.000 - R$ 10.000',
  '25k': 'R$ 10.000 - R$ 25.000',
  '50k': 'R$ 25.000 - R$ 50.000',
  '50k+': 'Acima de R$ 50.000',
  conversar: 'Prefere conversar'
};

const timelineLabels: Record<string, string> = {
  urgente: 'Urgente (até 2 semanas)',
  rapido: 'Rápido (2-4 semanas)',
  normal: 'Normal (1-2 meses)',
  flexivel: 'Flexível (2+ meses)'
};

const AdminContato = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<ContactMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: ['contact-messages'],
    queryFn: contactApi.getAll
  });

  const markAsReadMutation = useMutation({
    mutationFn: contactApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-messages'] });
    }
  });

  const handleConfirmDelete = async () => {
    if (messageToDelete) {
      setIsDeleting(true);
      try {
        await contactApi.delete(messageToDelete.id);
        queryClient.invalidateQueries({ queryKey: ['contact-messages'] });
        toast({
          title: 'Mensagem excluída',
          description: 'A mensagem foi excluída com sucesso.'
        });
        setMessageToDelete(null);
      } catch (error) {
        toast({
          title: 'Erro',
          description: 'Não foi possível excluir a mensagem.',
          variant: 'destructive'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleOpenMessage = (message: ContactMessage) => {
    setSelectedMessage(message);
    if (!message.read) {
      markAsReadMutation.mutate(message.id);
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(message =>
    message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (message.company && message.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-center py-20">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Mail className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-red-900">Erro ao carregar mensagens</h2>
        <p className="text-red-700">Ocorreu um erro ao carregar as mensagens de contato.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inbox</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie suas mensagens e oportunidades
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="default" className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm shadow-lg">
            {unreadCount} nova{unreadCount !== 1 && 's'} mensagem{unreadCount !== 1 && 'ns'}
          </Badge>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 shadow-lg overflow-hidden backdrop-blur-sm bg-card/50">
          <CardHeader className="border-b bg-muted/20 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              {/* Search Bar */}
              <div className="relative flex-1 max-w-md group">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Buscar por nome, email ou mensagem..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-muted group-focus-within:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>{filteredMessages.length} mensagens</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[300px] pl-6">Remetente</TableHead>
                  <TableHead className="max-w-[400px]">Mensagem</TableHead>
                  <TableHead>Interesse</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {filteredMessages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center h-40 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-3">
                            <Mail className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                          <p className="font-medium">Caixa de entrada vazia</p>
                          <p className="text-sm text-muted-foreground/70">
                            {searchTerm ? 'Nenhuma mensagem encontrada para sua busca.' : 'Ainda não chegaram novas mensagens.'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMessages.map((message, index) => (
                      <motion.tr
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: index * 0.05 }}
                        className={`group hover:bg-muted/40 transition-colors border-b border-border/50 last:border-0 ${!message.read ? "bg-blue-500/5 hover:bg-blue-500/10" : ""}`}
                        style={{ display: 'table-row' }}
                        layoutId={message.id}
                      >
                        <TableCell className="pl-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${!message.read ? 'bg-blue-600 animate-pulse' : 'bg-transparent'}`} title={!message.read ? "Não lida" : ""} />
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 overflow-hidden ring-1 ring-border shrink-0">
                              <User className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className={`text-sm truncate ${!message.read ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>{message.name}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-[180px]">{message.email}</span>
                              {message.company && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate max-w-[180px]">
                                  <Building2 className="h-3 w-3" /> {message.company}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[400px]">
                            <p className={`text-sm truncate ${!message.read ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              {message.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 items-start">
                            {message.service && (
                              <Badge variant="secondary" className="font-normal text-[10px] border bg-slate-100 text-slate-700 hover:bg-slate-200">
                                {serviceLabels[message.service] || message.service}
                              </Badge>
                            )}
                            {message.budget && (
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                {budgetLabels[message.budget] || message.budget}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col text-sm text-muted-foreground">
                            <span className="flex items-center gap-1 text-xs">
                              <Calendar className="h-3 w-3" />
                              {new Date(message.created_at).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="flex items-center gap-1 text-[10px] opacity-70">
                              <Clock className="h-3 w-3" />
                              {new Date(message.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-background">
                                  <span className="sr-only">Abrir menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => handleOpenMessage(message)} className="cursor-pointer">
                                  <Eye className="mr-2 h-4 w-4 text-blue-500" />
                                  Visualizar
                                </DropdownMenuItem>
                                {message.email && (
                                  <DropdownMenuItem asChild className="cursor-pointer">
                                    <a href={getEmailLink(`Re: Seu contato - ${message.service || 'Projeto'}`)}>
                                      <Mail className="mr-2 h-4 w-4 text-purple-500" /> Responder Email
                                    </a>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setMessageToDelete(message)}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      {/* Modal de Detalhes da Mensagem */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-2xl overflow-hidden border-0 shadow-2xl">
          <DialogHeader className="bg-muted/30 p-6 -m-6 mb-6 border-b">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                <User className="h-6 w-6" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  {selectedMessage?.name}
                </DialogTitle>
                <DialogDescription className="mt-1 flex items-center gap-2">
                  <Mail className="h-3 w-3" /> {selectedMessage?.email}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 bg-muted/20 p-4 rounded-lg">
                {selectedMessage.phone && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Telefone</label>
                    <p className="flex items-center gap-2 font-medium">
                      <Phone className="h-4 w-4 text-blue-500" />
                      {selectedMessage.phone}
                    </p>
                  </div>
                )}
                {selectedMessage.company && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Empresa</label>
                    <p className="flex items-center gap-2 font-medium">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      {selectedMessage.company}
                    </p>
                  </div>
                )}
                {selectedMessage.service && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Serviço de Interesse</label>
                    <Badge variant="secondary" className="mt-1">
                      {serviceLabels[selectedMessage.service] || selectedMessage.service}
                    </Badge>
                  </div>
                )}
                {selectedMessage.budget && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Orçamento Estimado</label>
                    <p className="flex items-center gap-2 font-medium text-green-600">
                      <DollarSign className="h-4 w-4" />
                      {budgetLabels[selectedMessage.budget] || selectedMessage.budget}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground mb-2 block flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  Mensagem
                </label>
                <div className="bg-muted/10 p-4 rounded-xl border text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  <p className="whitespace-pre-wrap">{selectedMessage.description}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-right flex items-center justify-end gap-1">
                  <Clock className="h-3 w-3" />
                  Enviada em {formatDate(selectedMessage.created_at)}
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                {selectedMessage.phone && (
                  <Button variant="outline" asChild className="gap-2">
                    <a href={getWhatsAppLink(`Olá ${selectedMessage.name}! Recebi sua mensagem pelo site.`)} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 text-green-600" />
                      WhatsApp
                    </a>
                  </Button>
                )}
                <Button asChild className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <a href={getEmailLink(`Re: Seu projeto - ${selectedMessage.service || 'Contato'}`)}>
                    <Mail className="h-4 w-4" />
                    Responder por Email
                  </a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <DeleteAlertDialog
        open={!!messageToDelete}
        onOpenChange={(open) => !open && setMessageToDelete(null)}
        title="Excluir mensagem?"
        description={`Tem certeza que deseja excluir a mensagem de ${messageToDelete?.name}? Esta ação não pode ser desfeita.`}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default AdminContato;
